import { useState, useEffect } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import "../static/header-styles.css";
import CustomAutocomplete from "./CustomAutocomplete";  
import {
	Typography,
	CardContent,
	Box,
	TextField,
} from "@mui/material";
import ProjectSelect from "./ProjectViewComponents/ProjectSelectComponent";
import CompanySelect from "./ProjectViewComponents/CompanySelectorComponent";
import ProjectSettingsModal from "./ProjectViewComponents/ProjectSettingsContainerModal";
import FAQModal from "./ProjectViewComponents/FAQComponent";
import TutorialModal from "./ProjectViewComponents/TutorialModalComponent";
import { setTab, setHolidayMode } from "../../../store/slices/globalSlice";
import {
	setProjectData,
	setBudgetData,
	setTeamData,
	setSelectedProject,
	setSettingsData,
	setProjects,
	setCompanies,
	setUsers,
	setSelectedCompany,
} from "../../../store/slices/projectSlice";
import {
	setSelectedPortfolioCompany,
	setSelectedOpsLeaders,
	setSelectedMonth,
	setPortfolioSearchTerm,
} from "../../../store/slices/portfolioSlice";
import { fetchProjectsAndCompanies } from "../Containers/api";
import { toast } from "react-hot-toast";
import { toastStyle } from "../Containers/continerUtilityFunctions";

const useReduxSelectors = () =>
	useSelector(
		(state) => ({
			projectData: state.project.projectData,
			authenticated: state.global.authenticated,
			budgetData: state.project.budgetData,
			teamData: state.project.teamData,
			revenueSpreadData: state.project.revenueSpreadData,
			settingsData: state.project.settingsData,
			invoiceData: state.project.invoiceData,
			projects: state.project.projects,
			companies: state.project.companies,
			selectedCompany: state.project.selectedCompany,
			selectedProject: state.project.selectedProject,
			selectedProjectId: state.project.selectedProjectId,
			tab: state.global.tab,
			isHolidayMode: state.global.isHolidayMode,
			isLoading: state.global.isLoading,
			opsLeaderOptions: state.portfolio.opsLeaderOptions || [],
			monthOptions: state.portfolio.monthOptions || [],
			selectedPortfolioCompany: state.portfolio.selectedPortfolioCompany || [],
			selectedOpsLeaders: state.portfolio.selectedOpsLeaders || [],
			selectedMonth: state.portfolio.selectedMonth || '',
			portfolioSearchTerm: state.portfolio.portfolioSearchTerm || '',
		}),
		shallowEqual
	);

const SnowGlobeIcon = () => (
	<svg
		width="20"
		height="20"
		viewBox="0 0 24 24"
		role="img"
		aria-hidden="true"
		focusable="false"
		style={{ display: "block" }}
	>
		<path
			d="M12 2a7 7 0 0 0-7 7c0 3.15 1.94 5.95 4.73 7.05L10 16.9V19h4v-2.95l.27-.12C17.06 14.95 19 12.15 19 9a7 7 0 0 0-7-7zm0 2a5 5 0 0 1 5 5c0 2.02-1.19 3.82-3.12 4.66l-1.52.66V17h-2v-3.68l-1.52-.66C8.19 12.82 7 11.02 7 9a5 5 0 0 1 5-5z"
			fill="currentColor"
		/>
		<path d="M7 20h10a1 1 0 0 1 1 1v1H6v-1a1 1 0 0 1 1-1z" fill="currentColor" />
		<circle cx="10" cy="9" r="1" fill="currentColor" />
		<circle cx="14.5" cy="10.5" r="1" fill="currentColor" />
		<circle cx="15" cy="7" r="0.8" fill="currentColor" />
		<circle cx="9" cy="12.5" r="0.6" fill="currentColor" />
	</svg>
);

const HeaderComponent = ({
	// Settings Modal
	selectedSnapshots,
	initialSnapshots,
	// Portfolio Filters -- To be moved to redux
	handlePortfolioSearch,
}) => {
	// Handling Tab Updates to Redux Store Here
	const dispatch = useDispatch();
	const {
		projectData,
		selectedProject,
		selectedProjectId,
		tab,
		isHolidayMode,
		settingsData,
		companies,
		selectedCompany,
		isLoading,
		opsLeaderOptions,
		monthOptions,
		selectedPortfolioCompany,
		selectedOpsLeaders,
		selectedMonth,
		portfolioSearchTerm,
	} = useReduxSelectors();

	const handleTabChange = (newTab) => {
		dispatch(setTab(newTab));
	};
	const handleHolidayModeToggle = () => {
		dispatch(setHolidayMode(!isHolidayMode));
	};
	const renderHolidayModeToggle = () => (
		<Box
			component="button"
			type="button"
			onClick={handleHolidayModeToggle}
			aria-pressed={isHolidayMode}
			sx={{
				display: "inline-flex",
				alignItems: "center",
				gap: "6px",
				background: "transparent",
				border: "none",
				padding: 0,
				color: isHolidayMode ? "#A5F3FC" : "#FFFFFF",
				cursor: "pointer",
				font: "inherit",
				transition: "color 0.2s ease",
				"&:hover, &:focus-visible": {
					color: "#A5F3FC",
				},
				"&:focus-visible": {
					outline: "2px solid rgba(165, 243, 252, 0.85)",
					borderRadius: "4px",
					outlineOffset: "4px",
				},
			}}
		>
			&nbsp;
			<Typography
				variant="subtitle1"
				className="titillium-web project-header-title-text"
				sx={{
					textDecoration: "underline",
					fontSize: "1rem",
					color: "currentColor",
				}}
			>
				Holiday Mode
			</Typography>
			<SnowGlobeIcon />
		</Box>
	);
	// Latest Snapshot for Data Updated
	const [mostRecentCreatedAt, setMostRecentCreatedAt] = useState(null);

	// Pull Project and Companies on First Mount
	useEffect(() => {
		const fetchProjectAndCompanyData = async () => {
			const companyFilterArray = selectedCompany ? [selectedCompany.company] : [];
			try {
				const data = await fetchProjectsAndCompanies(companyFilterArray);
				dispatch(setProjects(data.projects || []));
				dispatch(setCompanies(data.companies || []));
				dispatch(setUsers(data.users || []));
				if (selectedProjectId) {
					const selectedProject = data.projects.find(
						(project) => project.id === selectedProjectId
					);
					dispatch(setSelectedProject(selectedProject));
				}
			} catch (error) {
				console.error("Error fetching proj & company data in ProjectView:", error);
				toast.error(error.message || "An unexpected error occurred.", toastStyle());
			}
		};
		fetchProjectAndCompanyData();
	}, [dispatch, selectedCompany, selectedProjectId]);

	// Handle company selection change -- Project View
	const handleCompanySelectChangeForProjectView = (event, newValue) => {
		dispatch(setSelectedCompany(newValue));
		dispatch(setSelectedProject(null)); // Reset selected project when company changes
	};

	useEffect(() => {
		if (selectedSnapshots && selectedSnapshots.length > 0) {
			// Find the last snapshot that is "selected"
			const latestSelectedSnapshot = [...selectedSnapshots].reverse().find(item => item.selected);
			setMostRecentCreatedAt(latestSelectedSnapshot.name || null);
		} else {
			setMostRecentCreatedAt(null);
		}
	}, [selectedSnapshots]);

	return (
		<div>
			{/* Upper Section Container */}
			<div className="header-tabs-bar">
				<div className="dashboard-toggle-group" role="tablist" aria-label="view control tabs">
					{[
						{ key: "portfolio", label: "PORTFOLIO VIEW" },
						{ key: "project", label: "PROJECT VIEW" },
					].map(({ key, label }) => (
						<button
							key={key}
							type="button"
							className={`dashboard-toggle${tab === key ? " dashboard-toggle-active" : ""}`}
							onClick={() => handleTabChange(key)}
						>
							{label}
						</button>
					))}
				</div>
			</div>
		<CardContent
			className="header-container-card"
		>
			<div className="inner-container">
			<div className="header-upper-section title-block-content">
				<div
					className="dashboard-title"
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-start",
						flexDirection: "column",
						gap: "1px",
						...(tab === "portfolio" ? { width: "100%", minWidth: 0 } : {}),
					}}
				>
					<div
						className={`dashboard-selectors-container${tab === "portfolio" ? " is-portfolio" : ""}`}
					>
						{/* Project/Operations View */}
						{tab === "project" && (
							<>
								<div className="dashboard-selector" style={{ flex: 1 }}>
									<CompanySelect
										selectedCompany={selectedCompany}
										options={companies}
										handleSelectChange={handleCompanySelectChangeForProjectView}
									/>
								</div>
								<div className="dashboard-selector" style={{ flex: 1 }}>
									<ProjectSelect />
								</div>
							</>
						)}
						{/* Portfolio View */}
						{tab === "portfolio" && (
							<>
								<div
									className="overall-portfolio-wrapper"
									style={{
										display: "flex",
										flexDirection: "row",
										justifyContent: "stretch",
										alignItems: "center",
										width: "100%",
									}}
								>
									<div
										className="pv-filters-container"
										style={{
											display: "flex",
											flexDirection: "row",
											flexWrap: "wrap",
											rowGap: "8px",
											columnGap: "8px",
											justifyContent: "flex-start",
											padding: "7px",
											alignItems: "center",
											flex: "1 1 100%",
											width: "100%",
											minWidth: 0,
										}}
									>
										<div className="dashboard-selector" style={{ flex: 1, marginLeft: 5 }}>
											<CustomAutocomplete
												label="Cost Status Period"
												options={monthOptions}
												selectedValues={selectedMonth}
												getOptionId={(o) => (typeof o === 'string' ? o : o.id ?? o.value ?? o.label)}
												onApply={(vals) => dispatch(setSelectedMonth(vals || "Latest"))}
												isLoading={isLoading}
												multiple={false}
											/>
										</div>
										<div style={{ 
											fontSize: "18px", 
											color: "white", 
											margin: "0 7px",
											alignSelf: "center" 
										}}>
											|
										</div>
										<div className="dashboard-selector" style={{ flex: 1 }}>
											<CustomAutocomplete
												label="Company"
												options={companies.map((company) => company.company)}
												selectedValues={selectedPortfolioCompany}
												getOptionId={(o) => (typeof o === 'string' ? o : o.id ?? o.value ?? o.label)}
												onApply={(vals) => dispatch(setSelectedPortfolioCompany(vals))}
												isLoading={isLoading}
											/>
										</div>
										<div className="dashboard-selector" style={{ flex: 1 }}>
											<CustomAutocomplete
												label="Ops Leader"
												options={opsLeaderOptions}
												selectedValues={selectedOpsLeaders}
												getOptionId={(o) => (typeof o === 'string' ? o : o.id ?? o.value ?? o.label)}
												onApply={(vals) => dispatch(setSelectedOpsLeaders(vals))}
												isLoading={isLoading}
											/>
										</div>
										<div style={{ flex: '0 0 220px' }}>
											<TextField
												variant="outlined"
												size="small"
												placeholder="Search project..."
												value={portfolioSearchTerm}
												onChange={(e) => dispatch(setPortfolioSearchTerm(e.target.value))}
												sx={{
													width: '100%',
													'& .MuiOutlinedInput-root': {
														backgroundColor: 'white',
														borderRadius: 1,
														'& fieldset': { borderColor: '#90caf9' },
													},
													'& .MuiInputBase-input': {
														fontSize: '0.8rem',
													},
												}}
											/>
										</div>
									</div>
								</div>
							</>
						)}
					</div>
				</div>
				{tab === "project" && (selectedProject ? (
					<div
						className="project-information-container"
						style={{ marginTop: "10px" }}
					>
						<Typography variant="h6" className="titillium-web-base project-header-title-text-large">
							{selectedProject.name} |
							<span
								title={projectData.project_data?.account_name}
								style={{ cursor: "pointer" }}
							>
								{projectData.project_data?.account_name?.length > 30 ? ` ${projectData.project_data.account_name.slice(0,30)}...`: ` ${projectData.project_data?.account_name}`}
							</span>
						</Typography>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: "8px",
							}}
						>
							<Typography className="titillium-web-base project-header-title-text">
								ARCO ID: {projectData.project_data?.arco_id} | {projectData.project_data?.project_number}
							</Typography>
							{/* Badge Logic */}
							{settingsData?.other_meta_data?.contract_structure === "GMP" && (
								<div className="badge titillium-web-base project-header-title-text-small contract-structure-gmp">
									GMP
								</div>
							)}
							{settingsData?.other_meta_data?.contract_structure === "Lump Sum" && (
								<div
									className="badge titillium-web-base project-header-title-text-small contract-structure-lumpsum"
								>
									Lump Sum
								</div>
							)}
						</div>
					</div>
				) : (
					<div
						className="project-information-container"
						style={{ marginBottom: "20px" }}
					>
						<Typography
							variant="h6"
							className="titillium-web-base project-header-title-text"
							style={{
								marginTop: "20px",
								textAlign: "center",
							}}
						>
							Please Select a Project to View Data.
						</Typography>
					</div>
				))}
				{tab === "project" && selectedProject && (
					<div className="project-links-container" style={{ flex: 1 }}>
						<Typography
							variant="h6"
							className="titillium-web-base project-header-title-text"
							sx={{marginRight: "10px"}}
						>	
							{selectedSnapshots && selectedSnapshots.length > 0 && mostRecentCreatedAt
								? `Displaying: ${mostRecentCreatedAt}`
								: "Latest Budget Snapshot: Loading..."}
						</Typography>
						<div className="project-settings-modal-container">
							<>
								<ProjectSettingsModal
									settingsData={settingsData}
									initialSnapshots={initialSnapshots}
									selectedSnapshots={selectedSnapshots}
									setTeamData={setTeamData}
									setBudgetData={setBudgetData}
									setProjectData={setProjectData}
									setSettingsData={setSettingsData}
								/>
								|&ensp;
							</>
							<FAQModal title={"Frequently Asked Questions"} />
								|
							<TutorialModal title={"Guide to using the Project Health Dashboard"} />
								|
							{renderHolidayModeToggle()}
						</div>
					</div>
				)}
				{tab === "project" && !selectedProject && (
					<div className="faq-tutorial-container">
						<FAQModal title={"Frequently Asked Questions"} />
						|
						<TutorialModal title={"Guide to using the Project Health Dashboard"}/>
						|
						{renderHolidayModeToggle()}
					</div>
				)}
			</div>
			</div>
		</CardContent>
		</div>
	);
};

export default HeaderComponent;
